import { useEffect, useState } from "react";
import axios from "axios";
import { Trash2, Search, RefreshCw, Eye } from "lucide-react";
import { authHeader } from "@/hooks/useAdminAuth";
import API from "@/lib/api";

const STATUS_STYLES = {
  new: "bg-[#00A8E8]/15 text-[#00A8E8] border-[#00A8E8]/25",
  reviewed: "bg-yellow-400/15 text-yellow-400 border-yellow-400/25",
  replied: "bg-green-400/15 text-green-400 border-green-400/25",
};

export default function AdminContacts() {
  const [contacts, setContacts] = useState([]);
  const [total, setTotal] = useState(0);
  const [page, setPage] = useState(1);
  const [pages, setPages] = useState(1);
  const [search, setSearch] = useState("");
  const [statusFilter, setStatusFilter] = useState("");
  const [loading, setLoading] = useState(true);
  const [selected, setSelected] = useState(null);

  const load = (p = 1) => {
    setLoading(true);
    const params = new URLSearchParams({ page: p, limit: 15 });
    if (search) params.set("search", search);
    if (statusFilter) params.set("status", statusFilter);
    axios
      .get(`${API}/contacts?${params}`, { headers: authHeader() })
      .then(({ data }) => {
        setContacts(data.data);
        setTotal(data.total);
        setPage(data.page);
        setPages(data.pages);
      })
      .catch(console.error)
      .finally(() => setLoading(false));
  };

  useEffect(() => {
    load();
  }, [statusFilter]); // eslint-disable-line react-hooks/exhaustive-deps

  const handleSearch = (e) => {
    e.preventDefault();
    load(1);
  };

  const changeStatus = async (id, status) => {
    try {
      await axios.patch(`${API}/contacts/${id}/status`, { status }, { headers: authHeader() });
      setContacts((cs) => cs.map((c) => (c.id === id ? { ...c, status } : c)));
    } catch (err) {
      console.error(err);
    }
  };

  const deleteContact = async (id) => {
    if (!window.confirm("Delete this contact submission? This cannot be undone.")) return;
    try {
      await axios.delete(`${API}/contacts/${id}`, { headers: authHeader() });
      setContacts((cs) => cs.filter((c) => c.id !== id));
      setTotal((t) => t - 1);
      if (selected?.id === id) setSelected(null);
    } catch (err) {
      console.error(err);
    }
  };

  return (
    <div>
      <div className="flex items-center justify-between mb-6">
        <div>
          <h1 className="text-2xl font-bold text-white font-display">Contacts</h1>
          <p className="text-gray-500 mt-1 text-sm">{total} total submissions</p>
        </div>
        <button
          onClick={() => load(page)}
          className="p-2 text-gray-400 hover:text-white transition-colors"
          title="Refresh"
        >
          <RefreshCw className="w-4 h-4" />
        </button>
      </div>

      {/* Filters */}
      <div className="flex flex-wrap gap-3 mb-6">
        <form onSubmit={handleSearch} className="flex gap-2 flex-1 min-w-[200px]">
          <div className="relative flex-1">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-500" />
            <input
              type="text"
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              placeholder="Search name, email, company…"
              className="w-full bg-white/5 border border-white/10 rounded-xl pl-10 pr-4 py-2.5 text-white text-sm placeholder-gray-600 focus:outline-none focus:border-[#7B2CBF]/60"
            />
          </div>
          <button
            type="submit"
            className="px-4 py-2.5 bg-[#7B2CBF]/20 border border-[#7B2CBF]/30 text-[#7B2CBF] rounded-xl text-sm font-medium hover:bg-[#7B2CBF]/30 transition-colors"
          >
            Search
          </button>
        </form>

        <select
          value={statusFilter}
          onChange={(e) => setStatusFilter(e.target.value)}
          className="bg-white/5 border border-white/10 rounded-xl px-4 py-2.5 text-white text-sm focus:outline-none focus:border-[#7B2CBF]/60"
        >
          <option value="" className="bg-[#0D0E13]">All Status</option>
          <option value="new" className="bg-[#0D0E13]">New</option>
          <option value="reviewed" className="bg-[#0D0E13]">Reviewed</option>
          <option value="replied" className="bg-[#0D0E13]">Replied</option>
        </select>
      </div>

      {loading ? (
        <div className="flex items-center justify-center h-40">
          <div className="w-8 h-8 border-2 border-[#7B2CBF] border-t-transparent rounded-full animate-spin" />
        </div>
      ) : contacts.length === 0 ? (
        <div className="text-center py-16 text-gray-500">No contact submissions found.</div>
      ) : (
        <div className="space-y-2">
          {contacts.map((c) => (
            <div
              key={c.id}
              className="bg-[#0D0E13] border border-white/5 rounded-xl overflow-hidden"
            >
              <div className="flex items-center gap-4 px-5 py-4">
                <div className="flex-1 min-w-0">
                  <div className="flex items-center gap-3 flex-wrap">
                    <span className="text-white font-medium text-sm">{c.full_name}</span>
                    {c.company && <span className="text-gray-500 text-xs">· {c.company}</span>}
                    <span
                      className={`text-xs px-2 py-0.5 rounded-full border font-medium ${STATUS_STYLES[c.status]}`}
                    >
                      {c.status}
                    </span>
                  </div>
                  <div className="text-gray-500 text-xs mt-1 flex gap-3 flex-wrap">
                    <span>{c.email}</span>
                    {c.country && <span>· {c.country}</span>}
                    <span>· {c.inquiry_type}</span>
                    <span>· {new Date(c.created_at).toLocaleDateString()}</span>
                  </div>
                </div>

                <div className="flex items-center gap-2 shrink-0">
                  <button
                    onClick={() => setSelected(selected?.id === c.id ? null : c)}
                    className="p-2 text-gray-500 hover:text-white transition-colors"
                    title="View details"
                  >
                    <Eye className="w-4 h-4" />
                  </button>
                  <select
                    value={c.status}
                    onChange={(e) => changeStatus(c.id, e.target.value)}
                    className="bg-white/5 border border-white/10 rounded-lg px-2 py-1.5 text-white text-xs focus:outline-none"
                  >
                    <option value="new" className="bg-[#0D0E13]">New</option>
                    <option value="reviewed" className="bg-[#0D0E13]">Reviewed</option>
                    <option value="replied" className="bg-[#0D0E13]">Replied</option>
                  </select>
                  <button
                    onClick={() => deleteContact(c.id)}
                    className="p-2 text-gray-500 hover:text-red-400 transition-colors"
                    title="Delete"
                  >
                    <Trash2 className="w-4 h-4" />
                  </button>
                </div>
              </div>

              {selected?.id === c.id && (
                <div className="border-t border-white/5 px-5 py-4 bg-white/[0.02]">
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    <div>
                      <p className="text-xs text-gray-500 uppercase tracking-wider mb-1">Phone</p>
                      <p className="text-white text-sm">{c.phone || "—"}</p>
                    </div>
                    <div>
                      <p className="text-xs text-gray-500 uppercase tracking-wider mb-1">Preferred Mode</p>
                      <p className="text-white text-sm">{c.preferred_mode || "—"}</p>
                    </div>
                    <div className="md:col-span-2">
                      <p className="text-xs text-gray-500 uppercase tracking-wider mb-1">Summary</p>
                      <p className="text-white/80 text-sm leading-relaxed">{c.summary}</p>
                    </div>
                  </div>
                </div>
              )}
            </div>
          ))}
        </div>
      )}

      {/* Pagination */}
      {pages > 1 && (
        <div className="flex items-center justify-center gap-2 mt-6">
          {Array.from({ length: pages }, (_, i) => i + 1).map((p) => (
            <button
              key={p}
              onClick={() => load(p)}
              className={`w-8 h-8 rounded-lg text-sm font-medium transition-colors ${
                p === page
                  ? "bg-[#7B2CBF] text-white"
                  : "text-gray-400 hover:text-white hover:bg-white/5"
              }`}
            >
              {p}
            </button>
          ))}
        </div>
      )}
    </div>
  );
}
